import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FrontBanner from "@/assets/images/front-banner.svg";
import { ROUTESLIST } from "@/static/urls";
import { MEMBERSHIP_API_URL } from "@/static/api";
import "./success.scss";

/**
 * Public membership checkout success page.
 * Reads session_id from Stripe redirect, then backend creates user + DB record from Stripe session (email, etc.).
 */
const MembershipCheckoutSuccess = () => {
  const location = useLocation();
  const history = useHistory();
  const fromInPlace = location.state?.fromInPlace;
  const inPlaceMessage = location.state?.message;
  const [status, setStatus] = useState(
    fromInPlace ? "success" : "loading"
  );
  const [message, setMessage] = useState(
    fromInPlace
      ? (inPlaceMessage || "Payment successful. We've created your account — check the email you used to sign in.")
      : ""
  );

  const sessionId = new URLSearchParams(location.search).get("session_id");

  useEffect(() => {
    if (fromInPlace) return;

    if (!sessionId) {
      setStatus("error");
      setMessage("Missing session. If you just paid, check your email for confirmation.");
      return;
    }

    const confirmAndCreateUser = async () => {
      try {
        const res = await fetch(`${MEMBERSHIP_API_URL}/membership-checkout-success`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });
        const data = await res.json().catch(() => ({}));
        if (data?.success !== false && res.ok) {
          setStatus("success");
          setMessage(
            data?.message ||
              "Payment successful. We’ve created your account — check the email you used at checkout to sign in."
          );
        } else {
          setStatus("error");
          setMessage(data?.message || data?.error || "Something went wrong. Please contact support with your payment email.");
        }
      } catch (err) {
        setStatus("error");
        setMessage(err?.message || "We couldn’t complete your registration. Your payment was received — please contact support with the email you used to pay.");
      }
    };

    confirmAndCreateUser();
  }, [sessionId]);

  return (
    <main className="page page-shop membership-checkout-success-page">
      <div className="product-section">
        <div className="product-header" style={{ justifyContent: "space-between" }}>
          <div
            className="back-to-home-section"
            onClick={() => history.push(ROUTESLIST.PACKAGES)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && history.push(ROUTESLIST.PACKAGES)}
          >
            <ArrowBackIcon />
          </div>
          <div className="back-to-home-section-image">
            <img src="https://ample-samples.s3.us-east-2.amazonaws.com/app/front-banner.svg" alt="Ample Samples" />
          </div>
          <div>&nbsp;</div>
        </div>

        <div className="subscription-section">
          <div className="subscription-section-center membership-checkout-success-card">
            {status === "loading" && (
              <>
                <h3>Processing your membership…</h3>
                <p>We’re creating your account and recording your purchase. This may take a moment.</p>
                <div className="membership-checkout-success-loader" />
              </>
            )}
            {status === "success" && (
              <>
                <h3>Thank you for your purchase</h3>
                <p className="membership-checkout-success-message">{message}</p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => history.push(ROUTESLIST.LOGIN)}
                >
                  Go to sign in
                </button>
              </>
            )}
            {status === "error" && (
              <>
                <h3>Almost there</h3>
                <p className="membership-checkout-success-message membership-checkout-success-error">{message}</p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => history.push(ROUTESLIST.PACKAGES)}
                >
                  Back to packages
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default MembershipCheckoutSuccess;
